/**
 * Pure list-building for the Query Editor's "Query a table" picker. Reads the
 * Object Explorer's preloaded `DatabaseObject[]` (already fetched per tab for
 * @-mention autocomplete) and shapes it into the grouped `CommandPickerGroup`
 * form the VQE pickers render, so the picker looks and searches exactly like
 * the compact editor's FROM / JOIN pickers.
 *
 * Groups, in order: "Recent" (this session's last seeds on the same
 * connection + database — only once the list is long enough to need it, see
 * `RECENT_GROUP_MIN_OBJECTS`), then one group per schema when the database has
 * more than one, otherwise a single flat group. Views sit next to tables with
 * a small "view" badge rather than in a separate group — users search by
 * name, not by object kind.
 */
import type { DatabaseObject } from '../../../database-objects/models/databaseObjectTypes';
import type { CommandPickerGroup } from '../../visual-query-editor/render/common/CommandPickerPopover';

export type TableSeedKind = 'table' | 'view';

export type TableSeedTarget = {
  /** Stable id reported by the picker's onSelect. */
  readonly key: string;
  readonly schema: string;
  readonly name: string;
  readonly kind: TableSeedKind;
};

export type TableSeedGroup = CommandPickerGroup & {
  // The seed picker keys its own cmdk selection off `value`, so it is required here.
  readonly items: readonly (CommandPickerGroup['items'][number] & { readonly value: string; readonly target: TableSeedTarget })[];
};

export const MAX_RECENT_SEED_TABLES = 5;

/**
 * The "Recent" group only earns its space once the list scrolls: on a short
 * list every recent table is already in view a few rows down, and the Recent
 * copy just reads as a duplicate row. Shown when there are MORE than this many
 * tables + views; the recent seeds are still recorded either way.
 */
export const RECENT_GROUP_MIN_OBJECTS = 15;

export function tableSeedTargetKey(schema: string, name: string, kind: TableSeedKind): string {
  return `${kind}:${schema}.${name}`;
}

function toTarget(object: DatabaseObject): TableSeedTarget | null {
  const kind: TableSeedKind | null =
    object.type === 'table' ? 'table'
      : object.type === 'view' || object.type === 'materialized-view' ? 'view'
        : null;
  if (!kind) return null;
  return {
    key: tableSeedTargetKey(object.schema, object.name, kind),
    schema: object.schema,
    name: object.name,
    kind,
  };
}

function displayName(target: TableSeedTarget): string {
  return target.schema ? `${target.schema}.${target.name}` : target.name;
}

function toItem(target: TableSeedTarget, valuePrefix = ''): TableSeedGroup['items'][number] {
  const label = displayName(target);
  return {
    key: `${valuePrefix}${target.key}`,
    // cmdk requires unique values; the "Recent" copy of an item is prefixed
    // so it never collides with the same table in its schema group.
    value: `${valuePrefix}${label}`.toLowerCase(),
    label,
    meta: target.kind === 'view' ? 'view' : undefined,
    target,
  };
}

function byName(a: TableSeedTarget, b: TableSeedTarget): number {
  return a.name.localeCompare(b.name);
}

/**
 * `objects` — the tab's preloaded database objects (undefined while loading).
 * `recentKeys` — target keys of this session's recent seeds, newest first.
 */
export function tableSeedGroups(
  objects: readonly DatabaseObject[] | undefined,
  recentKeys: readonly string[] = [],
): readonly TableSeedGroup[] {
  if (!objects || objects.length === 0) return [];
  const targets: TableSeedTarget[] = [];
  for (const object of objects) {
    const target = toTarget(object);
    if (target) targets.push(target);
  }
  if (targets.length === 0) return [];

  const byKey = new Map(targets.map((t) => [t.key, t]));
  const groups: TableSeedGroup[] = [];

  const recent =
    targets.length > RECENT_GROUP_MIN_OBJECTS
      ? recentKeys
          .map((key) => byKey.get(key))
          .filter((t): t is TableSeedTarget => t !== undefined)
          .slice(0, MAX_RECENT_SEED_TABLES)
      : [];
  if (recent.length > 0) {
    groups.push({ heading: 'Recent', items: recent.map((t) => toItem(t, 'recent ')) });
  }

  const bySchema = new Map<string, TableSeedTarget[]>();
  for (const target of targets) {
    const bucket = bySchema.get(target.schema);
    if (bucket) bucket.push(target);
    else bySchema.set(target.schema, [target]);
  }
  if (bySchema.size <= 1) {
    groups.push({ items: targets.slice().sort(byName).map((t) => toItem(t)) });
    return groups;
  }
  for (const schema of Array.from(bySchema.keys()).sort((a, b) => a.localeCompare(b))) {
    groups.push({
      heading: schema.length > 0 ? schema : undefined,
      items: (bySchema.get(schema) ?? []).slice().sort(byName).map((t) => toItem(t)),
    });
  }
  return groups;
}

/** Resolve a picker `onSelect` key (recent-prefixed or not) back to its target. */
export function findTableSeedTarget(
  groups: readonly TableSeedGroup[],
  key: string,
): TableSeedTarget | null {
  for (const group of groups) {
    for (const item of group.items) {
      if (item.key === key) return item.target;
    }
  }
  return null;
}
